import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import api, { type Order } from '../lib/api'
import PremiumHeader from '../components/PremiumHeader'

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PLANNING: 'bg-indigo-100 text-indigo-800',
  READY: 'bg-green-100 text-green-800',
  POSTED_AS_LOAD: 'bg-blue-100 text-blue-800',
  DIRECT_ASSIGNED: 'bg-purple-100 text-purple-800',
  CLOSED: 'bg-slate-200 text-slate-700',
}

function placeSummary(snapshot: any): string {
  if (!snapshot) return '—'
  const city = snapshot.city ?? ''
  const state = snapshot.state ?? ''
  const zip = snapshot.zip ?? ''
  const text = [city, state].filter(Boolean).join(', ')
  return [text, zip].filter(Boolean).join(' ').trim() || '—'
}

export default function OrdersPage() {
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [customerFilter, setCustomerFilter] = useState('')
  const [readyDateFilter, setReadyDateFilter] = useState('')

  const q = useQuery({
    queryKey: ['orders', searchInput, statusFilter],
    queryFn: () => api.orders.list({ q: searchInput || undefined, status: statusFilter === 'ALL' ? undefined : statusFilter }).then((r) => r.data),
  })

  const orders: Order[] = q.data ?? []
  const customers = useMemo(() => {
    return Array.from(new Set(orders.map((order) => order.customerName).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b))
  }, [orders])

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (customerFilter && order.customerName !== customerFilter) return false
      if (readyDateFilter) {
        const ready = order.readyDate ? new Date(order.readyDate).toISOString().slice(0, 10) : ''
        if (ready !== readyDateFilter) return false
      }
      return true
    })
  }, [orders, customerFilter, readyDateFilter])

  return (
    <div className="max-w-7xl mx-auto p-6">
      <PremiumHeader
        title="Orders"
        subtitle="Manage booked moves from intake through dispatch and invoice."
        primaryAction={
          <Link to="/orders/new" className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800">
            New Order
          </Link>
        }
      />

      <div className="mb-4 bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          className="border rounded px-3 py-2 text-sm md:col-span-2"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search order #, VIN, customer, reference"
        />
        <select className="border rounded px-3 py-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All Statuses</option>
          <option value="DRAFT">DRAFT</option>
          <option value="PLANNING">PLANNING</option>
          <option value="READY">READY</option>
          <option value="POSTED_AS_LOAD">POSTED_AS_LOAD</option>
          <option value="DIRECT_ASSIGNED">DIRECT_ASSIGNED</option>
          <option value="CLOSED">CLOSED</option>
        </select>
        <select className="border rounded px-3 py-2 text-sm" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
          <option value="">All Customers</option>
          {customers.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <div className="md:col-span-2" />
        <input
          type="date"
          className="border rounded px-3 py-2 text-sm"
          value={readyDateFilter}
          onChange={(e) => setReadyDateFilter(e.target.value)}
        />
      </div>

      {q.isLoading && (
        <div className="text-sm text-gray-500 bg-white border rounded-xl p-6">Loading orders...</div>
      )}
      {q.isError && (
        <div className="text-sm text-red-600 bg-white border border-red-200 rounded-xl p-6">Failed to load orders.</div>
      )}

      {!q.isLoading && !filteredOrders.length && (
        <div className="text-sm text-gray-500 bg-white border rounded-xl p-6">No orders yet. Create your first order.</div>
      )}

      {!!filteredOrders.length && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">VIN Count</th>
                <th className="text-left px-3 py-2">First VIN / Vehicle</th>
                <th className="text-left px-3 py-2">Pickup</th>
                <th className="text-left px-3 py-2">Delivery</th>
                <th className="text-left px-3 py-2">Ready Date</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <Link to={`/orders/${order.id}`} className="text-blue-700 font-medium hover:underline">
                      {order.orderNumber}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{order.customerName ?? '—'}</td>
                  <td className="px-3 py-2">{order.vinCount ?? 0}</td>
                  <td className="px-3 py-2">
                    <div>{order.firstVin ?? '—'}</div>
                    <div className="text-xs text-gray-500">{order.firstVehicleSummary ?? '—'}</div>
                  </td>
                  <td className="px-3 py-2">{placeSummary(order.pickupSnapshot)}</td>
                  <td className="px-3 py-2">{placeSummary(order.deliverySnapshot)}</td>
                  <td className="px-3 py-2">{order.readyDate ? new Date(order.readyDate).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-100 text-gray-700'}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">{order.createdAt ? new Date(order.createdAt).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
